import styled, { css } from 'styled-components';
import { theme } from '@policygenius/mortar';
import { ReferralDesktopContainer } from '../styles';
import { fontWeight, suisseIntl, victorSerif } from '../typography';

export const ReferralHeaderBarInner = styled(ReferralDesktopContainer)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;

  @media (min-width: ${theme.breakpoints.medium}px) {
    gap: 20px;
    flex-wrap: nowrap;
  }
`;

export const HeroRoot = styled.div`
  position: relative;
`;

export const HeaderSentinel = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 1px;
  pointer-events: none;
`;

export const HeaderSpacer = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  flex-shrink: 0;
`;

export const HeroSection = styled.section`
  box-sizing: border-box;
`;

export const HeroContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 0 16px 32px;

  @media (min-width: ${theme.breakpoints.medium}px) {
    padding: 0 48px 48px;
  }

  @media (min-width: ${theme.breakpoints.large}px) {
    padding: 0 0 48px;
  }
`;

export const HeroMediaFrame = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: ${theme.breakpoints.medium}px) {
    position: relative;
    display: block;
    height: calc(100vh - 110px);
    max-height: 720px;
  }
`;

const heroBgLineDraw = css`
  @keyframes heroBgLineDraw {
    from {
      stroke-dashoffset: 1;
    }
    to {
      stroke-dashoffset: 0;
    }
  }
`;

export const HeroImage = styled.div`
  order: 2;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 100%;
  aspect-ratio: 1;
  margin-top: 0;
  border-radius: 12px;
  overflow: hidden;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  isolation: isolate;

  @media (min-width: ${theme.breakpoints.medium}px) {
    order: unset;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 0;
    width: auto;
    height: auto;
    aspect-ratio: unset;
    min-height: 0;
    margin-top: 0;
    border-radius: 12px;
  }
`;

export const HeroBackgroundLinesSvg = styled.svg`
  position: absolute;
  top: 0;
  left: 50%;
  z-index: 1;
  width: 165%;
  height: 100%;
  transform: translateX(-50%);
  pointer-events: none;
  overflow: visible;

  path {
    fill: none;
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
  }

  path:nth-of-type(1) {
    animation: heroBgLineDraw 1.8s cubic-bezier(0.4, 0, 0.2, 1) 0.5s forwards;
  }

  path:nth-of-type(2) {
    animation: heroBgLineDraw 2s cubic-bezier(0.4, 0, 0.2, 1) 0.95s forwards;
  }

  ${heroBgLineDraw}

  @media (min-width: ${theme.breakpoints.medium}px) {
    width: 102%;
    height: 100%;
    left: 50%;
    transform: translateX(-50%);
  }

  @media (prefers-reduced-motion: reduce) {
    path {
      stroke-dashoffset: 0;
      animation: none;
    }
  }
`;

export const HeroOverlayCard = styled.div`
  order: 1;
  position: relative;
  z-index: 2;
  width: 100%;
  background: ${theme.colors.white};
  border-radius: 0 0 16px 16px;
  box-shadow: 0 24px 40px rgba(19, 18, 18, 0.04);
  box-sizing: border-box;
  padding: 0 16px;

  @media (min-width: ${theme.breakpoints.medium}px) {
    order: unset;
    max-width: 720px;
    margin: 0;
    width: 100%;
    padding: 0;
    border-radius: 0 0 16px 0;
  }
`;

export const HeroCardInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 0 28px;
  box-sizing: border-box;

  @media (min-width: ${theme.breakpoints.medium}px) {
    gap: 24px;
    padding: 12px 32px 16px;
  }
`;

export const HeroTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

export const HeroTitle = styled.h1`
  ${victorSerif()};
  margin: 0;
  color: #131212;
  font-size: 34px;
  line-height: 1.1;
  letter-spacing: -0.02em;

  @media (min-width: ${theme.breakpoints.medium}px) {
    font-size: 64px;
    line-height: 1.1;
  }
`;

export const HeroTitleHighlight = styled.span`
  position: relative;
  display: inline;
  white-space: nowrap;
`;

export const HeroTitleUnderlineWrap = styled.span`
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 100%;
  line-height: 0;
  pointer-events: none;

  @media (min-width: ${theme.breakpoints.medium}px) {
    bottom: -6px;
  }
`;

const underlineDraw = css`
  @keyframes heroTitleUnderlineDraw {
    from {
      stroke-dashoffset: 1;
    }
    to {
      stroke-dashoffset: 0;
    }
  }
`;

export const HeroTitleUnderlineSvg = styled.svg`
  display: block;
  width: 100%;
  max-width: 120px;
  height: auto;

  path {
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
    animation: heroTitleUnderlineDraw 0.9s cubic-bezier(0.4, 0, 0.2, 1) 0.35s forwards;
  }

  ${underlineDraw}

  @media (min-width: ${theme.breakpoints.medium}px) {
    max-width: 228px;
  }
`;

export const HeroSubtitle = styled.p`
  ${suisseIntl(fontWeight.regular)};
  margin: 0;
  color: #131212;
  font-size: 18px;
  line-height: 1.3;

  @media (min-width: ${theme.breakpoints.medium}px) {
    font-size: 24px;
    line-height: 1.3;
  }
`;

export const HeroCtaPanel = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 0;
  padding: 0;
  background: #f7f5f3;
  border-radius: 12px;
  box-sizing: border-box;

  @media (min-width: ${theme.breakpoints.medium}px) {
    width: 232px;
  }
`;

export const HeroCtaButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const HeroCtaButton = styled.button`
  margin: 0;
  width: 100%;
  height: 54px;
  padding: 14px 20px;
  border: none;
  border-radius: 5px;
  ${suisseIntl(fontWeight.medium)};
  font-size: 16px;
  line-height: 1.3;
  color: #131212;
  background-color: #eb6424;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  -webkit-appearance: none;
  appearance: none;

  &:hover,
  &:focus-visible {
    background-color: #f78752;
    color: #131212;
    outline: none;
  }

  &:active {
    background-color: #eb6424;
    color: #131212;
  }

  svg {
    flex-shrink: 0;
    display: block;
  }
`;

export const HeaderCtaContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const HeaderGetQuotesButton = styled.button`
  margin: 0;
  padding: 14px 20px;
  border: none;
  border-radius: 5px;
  ${suisseIntl(fontWeight.medium)};
  font-size: 16px;
  line-height: 1.3;
  color: #131212;
  background-color: #eb6424;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 40px;

  &:hover,
  &:focus-visible {
    background-color: #f78752;
    color: #131212;
    outline: none;
  }

  &:active {
    background-color: #eb6424;
    color: #131212;
  }

  svg {
    flex-shrink: 0;
    display: block;
  }

  @media (min-width: ${theme.breakpoints.medium}px) {
    width: auto;
    height: 48px;
  }
`;

export const ReferralHeaderBar = styled.header<{ $isSticky: boolean }>`
  width: 100%;
  padding: 16px 20px;
  background: ${theme.colors.white};
  box-sizing: border-box;

  @media (min-width: ${theme.breakpoints.medium}px) {
    padding: 16px 48px;
    min-height: 84px;
  }

  ${({ $isSticky }) =>
    $isSticky &&
    css`
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 200;
      box-shadow: 0 2px 12px rgba(19, 18, 18, 0.08);
    `}
`;

export const LogoCluster = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  min-width: 0;

  @media (min-width: ${theme.breakpoints.medium}px) {
    flex-wrap: nowrap;
    gap: 16px;
  }
`;

export const PgLogoWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  svg {
    display: block;
    height: auto;
    max-height: 28px;
    width: auto;
  }
  @media (min-width: ${theme.breakpoints.medium}px) {
    svg {
      max-height: 40px;
      max-width: min(220px, 52vw);
    }
  }
`;

export const PartnershipPlus = styled.span`
  flex-shrink: 0;
  ${suisseIntl(fontWeight.medium)};
  font-size: 28px;
  line-height: 1;
  color: #131212;

  @media (min-width: ${theme.breakpoints.medium}px) {
    font-size: 36px;
    line-height: 36px;
  }
`;

export const PartnerHeaderLogo = styled.img`
  flex-shrink: 1;
  max-height: 42px;
  max-width: 130px;
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: left center;
  @media (min-width: ${theme.breakpoints.medium}px) {
    max-height: 44px;
    max-width: 220px;
  }
`;

export const HeaderCtaWrap = styled.div<{ $visible: boolean }>`
  display: none;
  flex-shrink: 0;
  align-items: center;
  width: 100%;

  @media (min-width: ${theme.breakpoints.medium}px) {
    display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
    width: auto;
  }
`;
